import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, Download, Eye, Trash2, FileText, Activity, Pencil, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";
import ContractEditModal from "@/components/ContractEditModal";
import OwnerNotifyModal from "@/components/OwnerNotifyModal";

// Date + CHF helpers (Swiss formatting)
const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso.length === 10 ? `${iso}T00:00` : iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtChfPrice = (n) => {
  const v = Number(n || 0);
  return Math.abs(v - Math.round(v)) < 0.005
    ? Math.round(v).toLocaleString("de-CH").replace(/,/g, "'") + ".–"
    : v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'");
};
const isVitoVehicle = (vid) => (vid || "").startsWith("mercedes_vito");

const VEHICLE_FILTERS = [
  { id: "", labelKey: "filter_all" },
  { id: "mercedes_vito", label: "Vito · BE 735 840" },
  { id: "mercedes_vito_wiesental", label: "Vito · SG 364 087" },
  { id: "mercedes_vito_neutral", label: "Vito · Wechselnummer" },
  { id: "renault_master", label: "Master · SO 119 475" },
  { id: "renault_master_2", label: "Master · SO 68 730" },
  { id: "renault_master_3", label: "Master · FR 359 701" },
  { id: "renault_master_neutral", label: "Master · Wechselnummer" },
];

const StatusPill = ({ status }) => {
  const map = {
    pending_review: { c: "bg-orange-100 text-orange-800 border border-orange-300", l: "Zur Prüfung" },
    pending: { c: "bg-[var(--rs-surface-2)] text-[var(--rs-text)]", l: "Bestätigt" },
    ready: { c: "bg-[var(--rs-accent)]/20 text-[var(--rs-accent)]", l: "Bereit" },
    handed_over: { c: "bg-[var(--rs-primary)] text-white", l: "Übergeben" },
    returned: { c: "bg-black text-white", l: "Zurück" },
    completed: { c: "bg-[var(--rs-primary)]/15 text-[var(--rs-primary)]", l: "Abgeschlossen" },
    cancelled: { c: "bg-[var(--rs-error)]/15 text-[var(--rs-error)]", l: "Storniert" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${m.c}`}>
      {m.l}
    </span>
  );
};

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [stations, setStations] = useState([]);
  const [editModal, setEditModal] = useState({ open: false, contract: null });
  const [ownerModal, setOwnerModal] = useState({ open: false, contract: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (vehicleFilter) params.vehicle_id = vehicleFilter;
      const [{ data: cs }, { data: st }] = await Promise.all([
        api.get("/contracts", { params }),
        api.get("/stats"),
      ]);
      setContracts(cs);
      setStats(st);
    } catch (e) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/vehicles").then(({ data }) => {
      setVehicles(data.vehicles || []);
      setStations(data.stations || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleFilter]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
      if (res.data && res.data.type && res.data.type.includes("json")) {
        const text = await res.data.text();
        throw new Error(text || "Server-Fehler");
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.contract_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "PDF-Download fehlgeschlagen";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          if (json?.detail) msg = String(json.detail);
        } else if (e?.response?.data?.detail) {
          msg = String(e.response.data.detail);
        }
      } catch {}
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("PDF error:", e);
    }
  };

  const removeOne = async (c) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Vertrag gelöscht");
      load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const confirmReservation = async (c) => {
    if (!window.confirm(`Reservation ${c.contract_number} bestätigen?\n\nDer Kunde erhält die Buchungsbestätigung mit PDF per E-Mail.`)) return;
    try {
      await api.post(`/contracts/${c.id}/confirm-reservation`);
      toast.success("Reservation bestätigt – Kunde wurde benachrichtigt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bestätigung fehlgeschlagen");
    }
  };

  const rejectReservation = async (c) => {
    const reason = window.prompt(
      `Reservation ${c.contract_number} ablehnen.\n\nOptional: Grund für den Kunden (wird in der Absage-Mail mitgeschickt):`,
      ""
    );
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/contracts/${c.id}/reject-reservation`, { reason: reason || null });
      toast.success("Reservation abgelehnt – Kunde wurde benachrichtigt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ablehnung fehlgeschlagen");
    }
  };

  const pendingReviews = contracts.filter((c) => c.status === "pending_review");

  const totals = stats?.totals || { contracts: 0, hours: 0, revenue: 0 };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="rs-overline">NIVARA · ADMIN</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight leading-[1]">
              Dashboard
            </h1>
            <p className="text-[var(--rs-text-muted)] mt-2">{t("admin_sub")}</p>
          </div>
          <Link to="/" className="rs-btn-secondary self-start" data-testid="admin-new-contract">
            <FileText size={16} className="mr-2" />
            {t("new_contract")}
          </Link>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mt-8 border-y-2 border-[var(--rs-text)]">
          <Stat label={t("stats_total_contracts")} value={totals.contracts} testid="stat-total" />
          <Stat label={t("stats_total_revenue")} value={Math.round(Number(totals.revenue)).toLocaleString("de-CH").replace(/,/g, "'")} accent testid="stat-revenue" />
          <Stat label={t("stats_total_hours")} value={Math.round(Number(totals.hours))} testid="stat-hours" />
          <Stat label={t("stats_status")} value={Object.keys(stats?.by_status || {}).length} testid="stat-status-count" />
        </div>

        {/* Pending reviews banner */}
        {pendingReviews.length > 0 && (
          <section className="mt-8 bg-orange-50 border-l-4 border-orange-500 p-5" data-testid="pending-reviews-banner">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="rs-overline !text-orange-700">Aktion erforderlich</div>
                <h2 className="font-[Chivo] text-xl sm:text-2xl font-black mt-1 text-orange-900">
                  {pendingReviews.length} {pendingReviews.length === 1 ? "Reservation wartet" : "Reservationen warten"} auf Bestätigung
                </h2>
                <p className="text-sm text-orange-900/80 mt-1">
                  Bestätige die Reservation, damit der Kunde die offizielle Buchungsbestätigung per E-Mail erhält.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Per-vehicle stats */}
        {stats?.by_vehicle?.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Activity size={16} className="text-[var(--rs-primary)]" />
              <div className="rs-overline">{t("stats_per_vehicle")}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {stats.by_vehicle.map((v) => {
                const isVito = (v.vehicle_id || "").startsWith("mercedes_vito");
                const unitLabel = isVito ? "Tage" : "Stunden";
                const unitValue = isVito
                  ? Number(v.total_days ?? Math.round((v.total_hours || 0) / 24)).toLocaleString("de-CH")
                  : Number(v.total_hours || 0).toFixed(1);
                return (
                <div key={v.vehicle_id} className="bg-[var(--rs-surface)] border-l-4 border-[var(--rs-primary)] p-5" data-testid={`vehicle-stat-${v.vehicle_id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div>
                      <div className="font-[Chivo] text-xl font-black">{v.vehicle_name}</div>
                      <div className="text-xs text-[var(--rs-text-muted)] mt-1">{v.vehicle_plate}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rs-text-muted)]">CHF</div>
                      <div className="font-[Chivo] text-2xl font-black text-[var(--rs-primary)]">
                        {Number(v.total_revenue).toLocaleString("de-CH")}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-5 pt-4 border-t border-[var(--rs-border)]">
                    <SmallStat label={unitLabel} value={unitValue} />
                    <SmallStat label="Verträge" value={v.total_contracts} />
                    <SmallStat label="Pauschal" value={v.daily_count} />
                    <SmallStat label="Stundentarif" value={v.hourly_count} />
                  </div>
                </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rs-text-muted)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="rs-input !pl-10"
              data-testid="admin-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {VEHICLE_FILTERS.map((f) => {
              const label = f.labelKey ? t(f.labelKey) : f.label;
              const active = f.id === vehicleFilter;
              return (
                <button
                  key={f.id || "all"}
                  type="button"
                  onClick={() => setVehicleFilter(f.id)}
                  className={`px-4 py-3 text-xs font-bold uppercase tracking-[0.15em] border ${
                    active
                      ? "bg-[var(--rs-primary)] text-white border-[var(--rs-primary)]"
                      : "border-[var(--rs-border)] text-[var(--rs-text-muted)] hover:border-[var(--rs-primary)]"
                  }`}
                  data-testid={`filter-${f.id || "all"}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 border-2 border-[var(--rs-text)] overflow-x-auto">
          <table className="w-full" data-testid="contracts-table">
            <thead>
              <tr className="bg-[var(--rs-primary)] text-white text-left">
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">{t("th_contract")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">{t("th_customer")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">{t("th_vehicle")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">Tarif</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">{t("th_period")}</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">Status</th>
                <th className="px-4 py-3 text-[11px] uppercase tracking-[0.15em]">{t("th_price")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center text-[var(--rs-text-muted)] py-10">…</td>
                </tr>
              ) : contracts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-[var(--rs-text-muted)] py-10" data-testid="empty-row">
                    {t("no_contracts")}
                  </td>
                </tr>
              ) : (
                contracts.map((c) => (
                  <tr key={c.id} className="border-t border-[var(--rs-border)] hover:bg-[var(--rs-surface)]" data-testid={`contract-row-${c.id}`}>
                    <td className="px-4 py-3 font-bold">{c.contract_number}</td>
                    <td className="px-4 py-3">{c.customer_name}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-sm">{c.vehicle_name}</div>
                      <div className="text-xs text-[var(--rs-text-muted)]">{c.vehicle_plate}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-bold uppercase">{c.rental_type === "hourly" || c.rental_type === "master_package" ? "Stunde" : "Pauschal"}</div>
                      {isVitoVehicle(c.vehicle_id) ? (
                        (c.days || c.duration_days) > 0 && (
                          <div className="text-[var(--rs-text-muted)]">{c.days || c.duration_days} {(c.days || c.duration_days) === 1 ? "Tag" : "Tage"}</div>
                        )
                      ) : (
                        c.duration_hours > 0 && (
                          <div className="text-[var(--rs-text-muted)]">{Number(c.duration_hours).toFixed(1)} h</div>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <div>{fmtDateTime(c.rental_start)}</div>
                      <div className="text-[var(--rs-text-muted)]">→ {fmtDateTime(c.rental_end)}</div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                    <td className="px-4 py-3 font-bold text-[var(--rs-primary)] whitespace-nowrap">CHF {fmtChfPrice(c.price_chf)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {c.status === "pending_review" && (
                          <>
                            <button
                              type="button"
                              className="rs-btn-primary !py-1.5 !px-2 !text-[10px] uppercase tracking-wider"
                              onClick={() => confirmReservation(c)}
                              data-testid={`confirm-reservation-${c.id}`}
                            >
                              ✓ Bestätigen
                            </button>
                            <button
                              type="button"
                              className="rs-btn-ghost !py-1.5 !px-2 !text-[10px] uppercase tracking-wider !text-[#B91C1C] border border-[#B91C1C]/30"
                              onClick={() => rejectReservation(c)}
                              data-testid={`reject-reservation-${c.id}`}
                            >
                              ✗ Ablehnen
                            </button>
                          </>
                        )}
                        <button type="button" className="rs-btn-ghost !text-xs" onClick={() => navigate(`/admin/contracts/${c.id}`)} data-testid={`view-${c.id}`}>
                          <Eye size={14} className="mr-1" /> {t("view")}
                        </button>
                        <button type="button" className="rs-btn-ghost !text-xs" onClick={() => downloadPdf(c)} data-testid={`pdf-${c.id}`}>
                          <Download size={14} className="mr-1" /> {t("pdf")}
                        </button>
                        <button type="button" className="rs-btn-ghost !text-xs" onClick={() => setEditModal({ open: true, contract: c })} data-testid={`admin-edit-${c.id}`}>
                          <Pencil size={14} className="mr-1" /> Bearbeiten
                        </button>
                        <button
                          type="button"
                          className={`rs-btn-ghost !text-xs ${c.owner_payout ? "!text-[var(--rs-primary)]" : ""}`}
                          onClick={() => setOwnerModal({ open: true, contract: c })}
                          title={c.owner_payout ? `Bereits benachrichtigt am ${new Date(c.owner_payout.notified_at).toLocaleDateString("de-CH")}` : "Besitzer melden"}
                          data-testid={`notify-owner-${c.id}`}
                        >
                          <Mail size={14} className="mr-1" /> Besitzer
                        </button>
                        <button type="button" className="rs-btn-ghost !text-xs hover:!text-[var(--rs-error)]" onClick={() => removeOne(c)} data-testid={`delete-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContractEditModal
        open={editModal.open}
        contract={editModal.contract}
        vehicles={vehicles}
        stations={stations}
        onClose={() => setEditModal({ open: false, contract: null })}
        onSaved={load}
      />

      <OwnerNotifyModal
        open={ownerModal.open}
        contract={ownerModal.contract}
        onClose={() => setOwnerModal({ open: false, contract: null })}
        onSent={load}
      />
    </div>
  );
};

const Stat = ({ label, value, accent, testid }) => (
  <div className="p-5 lg:p-6 border-r-2 border-[var(--rs-text)] last:border-r-0" data-testid={testid}>
    <div className="rs-overline">{label}</div>
    <div className={`font-[Chivo] text-3xl font-black mt-1 ${accent ? "text-[var(--rs-primary)]" : ""}`}>
      {value}
    </div>
  </div>
);

const SmallStat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">{label}</div>
    <div className="font-bold mt-1">{value}</div>
  </div>
);

export default AdminDashboard;
